/* ====================================================================
 *  hydrateImages — підстановка картинки варіанта в позицію кошика.
 *
 *  Навіщо: позицію могли зберегти без картинки, а для ексклюзивів
 *  потрібна саме картинка варіанта, а не товару.
 *  Мутує передані позиції на місці і (для залогінених) допише image
 *  назад у Firestore, щоб наступне завантаження вже було правильним.
 * ==================================================================== */

import type { Firestore } from "firebase/firestore";
import { fetchVariantDoc, patchImages } from "./remote";
import type { CartItem } from "./mapping";

type AnyRecord = Record<string, unknown>;

/** Чи треба тягнути картинку з варіанта. */
function needsHydrate(it: Partial<CartItem>): boolean {
  const img = String(it?.image || "").trim();
  if (!it || !it.productId || !it.variantId) return false;
  if (!img) return true;
  // Ексклюзив має показувати картинку саме варіанта.
  // Евристика: картинки варіантів лежать під `/variants/`.
  if (it.isExclusive && !img.includes("/variants/")) return true;
  return false;
}

/** Витягти URL картинки з документа варіанта (поля історично різні). */
function pickUrl(v: unknown): string {
  if (!v || typeof v !== "object") return "";
  const o = v as AnyRecord;

  const direct =
    o.image || o.imageUrl || o.mainImage || o.mainImageUrl || o.mainImagePath;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const imgs = o.images;
  if (Array.isArray(imgs) && imgs.length) {
    const first = imgs[0];
    if (typeof first === "string" && first.trim()) return first.trim();
    if (first && typeof first === "object") {
      const f = first as AnyRecord;
      const u = f.url || f.src || f.path;
      if (typeof u === "string" && u.trim()) return u.trim();
    }
  }

  const paths = o.imagePaths;
  if (Array.isArray(paths) && paths.length && typeof paths[0] === "string") {
    return (paths[0] as string).trim();
  }

  return "";
}

export async function hydrateMissingImages(
  db: Firestore | null | undefined,
  uid: string,
  list: CartItem[],
): Promise<void> {
  if (!db) return;

  const targets = (Array.isArray(list) ? list : []).filter(needsHydrate);
  if (!targets.length) return;

  const updates: { key: string; image: string }[] = [];

  for (const it of targets) {
    const data = await fetchVariantDoc(
      db,
      String(it.productId),
      String(it.variantId),
    );
    if (!data) continue;
    const url = pickUrl(data);
    if (!url) continue;
    it.image = url;
    updates.push({ key: it.key, image: url });
  }

  if (updates.length && uid) await patchImages(db, uid, updates);
}
